import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ActionsProvider } from '../../../PurchaseFlow/NewSeatedPurchaseWrapper/providers/SeatmapProvider/SeatmapWrapper/providers/ActionsProvider'
import { usePackageSeatmap } from '../PackageSeatmapProvider'
import { SeatMap } from '../../../PurchaseFlow/NewSeatedPurchaseWrapper/providers/SeatmapProvider/SeatmapWrapper/SeatMap'

import { placePackageIntoCart } from '../../../../utilities/api'
import { storeCartSession } from '../../../../utilities/cartSessionTracking'
import { getProcessingFeeFromChargeAmount } from '../../../../utilities/helpers'
import {
  packageAddedFeeLines,
  packageCustomFeeLines,
  packageFeesAreAddedOnTop,
  previewPackageCharge,
} from '../../../../utilities/packagePrice'

import { TooltipProvider } from '../../../PurchaseFlow/NewSeatedPurchaseWrapper/providers/SeatmapProvider/SeatmapWrapper/providers/TooltipProvider'
import useSeatmapStore from '../../../../stores/seatmapStore'
import { SvgSeatmap } from '../../../PurchaseFlow/NewSeatedPurchaseWrapper/providers/SeatmapProvider/SeatmapWrapper/SvgSeatmap'

export default function PackageSeatmapWrapper({ handleClose }) {
  // Zustand
  const setSeatLookupTable = useSeatmapStore(
    (state) => state.setSeatLookupTable
  )
  const setSectionLookupTable = useSeatmapStore(
    (state) => state.setSectionLookupTable
  )
  const selectedFromMap = useSeatmapStore((state) => state.selectedFromMap)
  const data = useSeatmapStore((state) => state.data)

  const navigate = useNavigate()
  const { eventPackage, purchaseLog, setSeatedError } = usePackageSeatmap()

  const [mapError, setMapError] = useState(false)
  const [checkingAvailability, setCheckingAvailability] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPrice, setTotalPrice] = useState(0)
  const [processingFee, setProcessingFee] = useState(0)
  const [feeLines, setFeeLines] = useState([])
  const [loadingComplete, setLoadingComplete] = useState(false)

  const checkout = () => {
    setCheckingAvailability(true)
    let data = {
      packageTickets: selectedFromMap,
      eventPackageId: eventPackage.id,
    }

    placePackageIntoCart(data)
      .then((res) => {
        console.log('placePackageIntoCart res: ', res.data)
        storeCartSession({
          cartId: res.data.cartId,
          sessionId: res.data.sessionId,
        })
        sessionStorage.setItem(
          'cart',
          JSON.stringify({ cartId: res.data.cartId })
        )
        navigate(`/checkout`)
      })
      .catch((err) => {
        if (err.response) {
          setSeatedError({
            status: err.response.status,
            title: 'Selected tickets not available',
            message: err.response.data.error.message || 'An error occurred',
            buttonText: 'Return to tickets list',
          })
        } else {
          setSeatedError({
            status: 500,
            title: 'Selected tickets not available',
            message: 'Network error occurred',
            buttonText: 'Return to tickets list',
          })
        }
      })
      .finally(() => {
        setCheckingAvailability(false)
      })
  }

  const createSeatLookupTable = (seatedTickets, eventPackage) => {
    const {
      ticket_groups,
      package_groups,
      package_tickets,
      ...packageWithoutTicketGroups
    } = eventPackage
    const packageTierPrice = eventPackage?.pricingTiers?.[0]?.price
    const lookupTable = {}
    const feesOnTop = packageFeesAreAddedOnTop(eventPackage)

    seatedTickets.forEach((ticket) => {
      // Create a single reference object for this ticket group
      const ticketGroupRef = {
        ...ticket,
        price: packageTierPrice ?? ticket?.price,
        resale: false,
        offer: {
          name: packageWithoutTicketGroups.name,
          feesOnTop,
        },
      }

      lookupTable[ticket.seatId] = ticketGroupRef
    })

    // Delete instances sold in sibling packages
    const soldInOtherPackages = purchaseLog.sold_package_tickets
    soldInOtherPackages.forEach((soldSeatId) => {
      if (lookupTable[soldSeatId]) {
        delete lookupTable[soldSeatId]
      }
    })

    return lookupTable
  }

  const createSectionLookupTable = (tickets, eventPackage) => {
    const { package_tickets, ...packageWithoutTicketGroups } = eventPackage
    const packageTierPrice = eventPackage?.pricingTiers?.[0]?.price
    const lookupTable = {}
    const feesOnTop = packageFeesAreAddedOnTop(eventPackage)

    tickets.forEach((ticket) => {
      const { sectionId, seatId } = ticket

      if (!lookupTable[sectionId]) {
        // Initialize first entry for this section
        lookupTable[sectionId] = [
          {
            ...ticket,
            price: packageTierPrice ?? ticket?.price,
            package: {
              name: packageWithoutTicketGroups.name,
              minQuantity: packageWithoutTicketGroups.minQuantity,
              maxQuantity: packageWithoutTicketGroups.maxQuantity,
              feesOnTop,
            },
            availableCount: 1,
          },
        ]
      } else {
        lookupTable[sectionId][0].availableCount += 1
      }
    })

    const soldInOtherPackages = purchaseLog?.section_sold_counts
    if (purchaseLog && soldInOtherPackages) {
      Object.keys(lookupTable).forEach((sectionId) => {
        const sectionEntry = lookupTable[sectionId][0]
        const soldCount = soldInOtherPackages.find(
          (soldCount) => soldCount.sectionId === sectionId
        )
        if (!soldCount) return

        const sectionCapacity = data.sections?.[sectionId]?.spots?.length
        if (!sectionCapacity) return

        const remainingCapacity = sectionCapacity - soldCount.soldCount
        if (remainingCapacity < sectionEntry.availableCount) {
          sectionEntry.availableCount = Math.max(0, remainingCapacity)
        }
      })
    }

    console.log('sectionlook: ', lookupTable)
    return lookupTable
  }

  useEffect(() => {
    // Calculate total price
    const newTotalPrice = selectedFromMap
      .map((ticketGroup) => {
        if (ticketGroup.GA) {
          return ticketGroup.quantity * ticketGroup.price
        } else {
          return ticketGroup.price
        }
      })
      .reduce((sum, price) => sum + price, 0)
    const seatedCount = selectedFromMap
      .filter((ticketGroup) => ticketGroup.GA === false)
      .map((ticketGroup) => ticketGroup.seatId).length
    const gaCount = selectedFromMap
      .filter((ticketGroup) => ticketGroup.GA === true)
      .reduce((sum, ticketGroup) => sum + ticketGroup.quantity, 0)

    const newTotalCount = seatedCount + gaCount
    setTotalCount(newTotalCount)
    setTotalPrice(newTotalPrice)

    const customFees = packageCustomFeeLines(eventPackage)
    const hasPackageFees =
      Boolean(eventPackage?.websiteSeatFees) || customFees.length > 0
    if (newTotalCount > 0 && hasPackageFees) {
      const charge = previewPackageCharge({
        ticketSubtotal: newTotalPrice,
        seatCount: newTotalCount,
        seatFees: eventPackage?.websiteSeatFees,
        customFees,
      })
      setProcessingFee(charge.processingFee)
      setFeeLines(packageAddedFeeLines(charge))
    } else {
      setProcessingFee(getProcessingFeeFromChargeAmount(newTotalPrice))
      setFeeLines([])
    }
  }, [selectedFromMap, eventPackage])

  useEffect(() => {
    let getMapAvailability = async () => {
      try {
        console.log('eventPackage: ', eventPackage)
        const packageTickets = eventPackage?.package_tickets
        if (!packageTickets) return

        const unSoldTickets = packageTickets.filter(
          (ticket) => ticket.on_sale_status === 'available'
        )
        const seatedTickets = unSoldTickets.filter((ticket) => !ticket.GA)

        // Create lookup tables; allows for constant time checks
        const sectionLookupTable = createSectionLookupTable(
          unSoldTickets,
          eventPackage
        )
        setSectionLookupTable(sectionLookupTable)

        const seatLookupTable = createSeatLookupTable(
          seatedTickets,
          eventPackage
        )
        setSeatLookupTable(seatLookupTable)

        setLoadingComplete(true)
        console.log('LOADING COMPLETED....')
      } catch (error) {
        console.log('ERROR in getSectionAvailability: ', error)
        setMapError(true)
        setLoadingComplete(true)
      }
    }

    getMapAvailability()
  }, [eventPackage])

  return (
    <ActionsProvider
      handleClose={handleClose}
      loadingComplete={loadingComplete}
      totalCount={totalCount}
      totalPrice={totalPrice}
      processingFee={processingFee}
      showProcessingFee={true}
      feeLines={feeLines}
      showBaseTicketPrice={feeLines.length > 0}
      isSeasonPackage={true}
      checkout={checkout}
      checkingAvailability={checkingAvailability}
    >
      <TooltipProvider>
        <SvgSeatmap
          loadingComplete={loadingComplete}
          mapError={mapError}
          setMapError={setMapError}
        />
      </TooltipProvider>
    </ActionsProvider>
  )
}
